import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material'
import { com } from '@eidu/web'
import { useMemo, type SyntheticEvent, type UIEvent } from 'react'
import { PermissionPolicyDTO } from '../../domain/permission/PermissionPolicyDTO'
import EqualityHashMap from '../../util/EqualityHashMap'
import PermissionPolicyId = com.eidu.sharedlib.entity.permission.PermissionPolicyId

type PolicyMultiSelectProps = {
  loadedPolicies: ReadonlyArray<PermissionPolicyDTO>
  selectedPolicies: ReadonlyArray<PermissionPolicyDTO>
  selectedPolicyIds: ReadonlyArray<PermissionPolicyId>
  onChange: (policyIds: ReadonlyArray<PermissionPolicyId>) => void
  hasMore: boolean
  loadMore: () => void
  loading?: boolean
}

const SCROLL_LOAD_MORE_THRESHOLD_PX = 32

const PolicyMultiSelect = ({
  loadedPolicies,
  selectedPolicies,
  selectedPolicyIds,
  onChange,
  hasMore,
  loadMore,
  loading,
}: PolicyMultiSelectProps) => {
  const optionsById = useMemo(() => {
    const map = new EqualityHashMap<PermissionPolicyId, PermissionPolicyDTO>()
    selectedPolicies.forEach((p) => map.set(p.policyId, p))
    loadedPolicies.forEach((p) => map.set(p.policyId, p))
    return map
  }, [loadedPolicies, selectedPolicies])

  const selectedIdSet = useMemo(() => {
    const map = new EqualityHashMap<PermissionPolicyId, true>()
    selectedPolicyIds.forEach((id) => map.set(id, true))
    return map
  }, [selectedPolicyIds])

  const sortedOptions = useMemo(
    () =>
      [...optionsById.values()]
        .filter((p) => !selectedIdSet.get(p.policyId))
        .sort((a, b) => a.policy.name.localeCompare(b.policy.name)),
    [optionsById, selectedIdSet]
  )

  const value = useMemo(
    () => selectedPolicyIds.flatMap((id) => (optionsById.get(id) ? [optionsById.get(id)!] : [])),
    [optionsById, selectedPolicyIds]
  )

  const handleListboxScroll = (event: UIEvent<HTMLUListElement>) => {
    if (!hasMore || loading) return
    const target = event.currentTarget
    if (target.scrollHeight - target.scrollTop - target.clientHeight < SCROLL_LOAD_MORE_THRESHOLD_PX) loadMore()
  }

  return (
    <Box sx={{ px: 2, pt: 1.5, pb: 2 }}>
      <Autocomplete
        multiple
        size="small"
        options={sortedOptions}
        value={value}
        loading={loading}
        getOptionLabel={(option) => option.policy.name}
        isOptionEqualToValue={(a, b) => a.policyId.equals(b.policyId)}
        onChange={(_: SyntheticEvent, next) => {
          const additions = next.map((v) => v.policyId).filter((id) => !selectedIdSet.get(id))
          if (additions.length === 0) return
          onChange([...selectedPolicyIds, ...additions])
        }}
        ListboxProps={{ onScroll: handleListboxScroll }}
        renderTags={() => null}
        disableCloseOnSelect
        renderInput={(params) => (
          <TextField
            {...params}
            label="Add policy"
            placeholder="Search policies to add"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.policyId.toString()}>
            <Typography variant="body2">{option.policy.name}</Typography>
          </li>
        )}
      />
    </Box>
  )
}

export default PolicyMultiSelect
